import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { REVIEW_ADDRESS_STATE_OPTIONS } from 'src/constants/document-review/constants'
import { DriverLicenseType } from 'src/constants/document-review/enums'
import type { DocumentReviewStep, ReviewFormValues } from 'src/interfaces/documentReview'
import { toFormDateString } from 'src/utils/documentReviewDates'

/** Steps whose name fields seed from the contractor on file rather than starting empty. */
const NAME_PREFILL_STEP_TYPES: CollectDataStepType[] = [
  CollectDataStepType.DriverLicense,
  CollectDataStepType.VehicleInsurance,
]

/** Mirrors the backend: only a `driver_license` step can carry a temporary license. */
export const isTemporaryDriverLicense = (step: DocumentReviewStep): boolean => {
  return (
    step.type === CollectDataStepType.DriverLicense &&
    step.partnerDriverCollectedData.metadata?.driverLicenseType === DriverLicenseType.Temporary
  )
}

const toFormString = (value: string | null | undefined): string => {
  return value ?? ''
}

/**
 * The State select only offers the 50 two-letter codes, so a value on file outside that list
 * (lowercase, DC, a full name) starts empty for the reviewer to pick rather than as a value the
 * select cannot display.
 */
const toStateOption = (value: string | null | undefined): string => {
  const stateCode = (value ?? '').trim().toUpperCase()
  const isListed = REVIEW_ADDRESS_STATE_OPTIONS.some((option) => {
    return option.value === stateCode
  })

  return isListed ? stateCode : ''
}

/**
 * The document's own expiry first (Retool's `metadata.expiration`), then the expiry already on the
 * collected data. A value that does not parse as a date is skipped, so the field starts empty
 * rather than holding garbage.
 */
const resolveInitialExpiresAt = (step: DocumentReviewStep): string => {
  const { documents, expiresAt } = step.partnerDriverCollectedData
  const documentExpiration = documents[0]?.metadata?.expiration

  const fromDocument =
    typeof documentExpiration === 'string' ? toFormDateString(documentExpiration) : undefined

  return fromDocument ?? toFormDateString(expiresAt ?? undefined) ?? ''
}

/**
 * Seeds the contractor review form from real response data only. A missing value leaves the field
 * empty — no placeholder is ever substituted.
 *
 * Names seed from the contractor on file on `driver_license` and `vehicle_and_insurance` steps (a
 * vehicle policy frequently names somebody else, so the contractor is the safer default). The
 * address seeds only for a temporary license, the one case it is shown and sent.
 *
 * `step.aiDocumentReview?.matchedData` is deliberately NOT read here. AI values reach the form only
 * through the explicit "Use these values" action, and only when `prefillSafe` is true — an
 * automatic seed would write unconfirmed extractions into an approval.
 */
export const buildContractorReviewInitialValues = (step: DocumentReviewStep): ReviewFormValues => {
  const { partnerDriver } = step.partnerDriverCollectedData
  const isNamePrefilled = NAME_PREFILL_STEP_TYPES.includes(step.type)
  const isAddressPrefilled = isTemporaryDriverLicense(step)

  return {
    firstName: isNamePrefilled ? toFormString(partnerDriver.firstName) : '',
    lastName: isNamePrefilled ? toFormString(partnerDriver.lastName) : '',
    documentId: '',
    dateIssued: '',
    expiresAt: resolveInitialExpiresAt(step),
    effectiveAt: '',
    addressStreet1: isAddressPrefilled ? toFormString(partnerDriver.addressStreet1) : '',
    addressStreet2: isAddressPrefilled ? toFormString(partnerDriver.addressStreet2) : '',
    addressCity: isAddressPrefilled ? toFormString(partnerDriver.addressCity) : '',
    addressState: isAddressPrefilled ? toStateOption(partnerDriver.addressState) : '',
    addressPostalCode: isAddressPrefilled ? toFormString(partnerDriver.addressPostalCode) : '',
  }
}
